// Static service & industry detail data referenced by translation keys
import {
  Building2,
  Sparkles,
  Wrench,
  Users,
  Truck,
  Home,
  Briefcase,
  ShoppingBag,
  HeartPulse,
  GraduationCap,
  ClipboardCheck,
  Hammer,
} from "lucide-react";

export const SERVICE_ICONS = {
  "facility-management": Building2,
  cleaning: Sparkles,
  maintenance: Wrench,
  manpower: Users,
  "snagging-services-uae": ClipboardCheck,
  "project-management-uae": Hammer,
};

export const INDUSTRY_ICONS = {
  logistics: Truck,
  residential: Home,
  commercial: Briefcase,
  retail: ShoppingBag,
  healthcare: HeartPulse,
  education: GraduationCap,
};

export const INDUSTRY_DETAILS = {
  residential: {
    eyebrow: "Residential FM · UAE",
    title_en: "Facility Management for Residential Towers & Villa Communities",
    title_ar: "إدارة المرافق للأبراج السكنية والمجمعات السكنية",
    subtitle_en: "Lifts, MEP, pools, landscape and common-area cleaning — under one SLA for owners' associations and developers in Dubai, Abu Dhabi & Ajman.",
    subtitle_ar: "المصاعد والكهروميكانيك والمسابح والمناظر الطبيعية وتنظيف المناطق المشتركة — ضمن اتفاقية واحدة لجمعيات الملاك والمطورين.",
    image: "https://customer-assets-rejwkqb3.emergentagent.net/job_orion-integrated-fm/artifacts/md7xadxo_WhatsApp%20Image%202026-04-22%20at%2012.26.03%20PM%20%284%29.jpeg",
    pillars_en: [
      { title: "Resident-Friendly Service", desc: "Quiet hours, lift etiquette, uniformed staff. Zero friction with tenants." },
      { title: "Asset Protection", desc: "Preventive PPM on lifts, pumps, MEP and common-area finishes extends asset life." },
      { title: "OA Reporting", desc: "Owners' Association-ready monthly KPI reports and AGM-ready audit trails." },
      { title: "24/7 Emergency", desc: "Leak, power-out, lift entrapment — dispatched within 30 minutes, 24/7." },
    ],
    scope_en: [
      "Lift and escalator AMC",
      "Swimming pool and gym housekeeping",
      "Landscape and irrigation",
      "MEP, plumbing and electrical PPM",
      "Fire alarm, sprinkler and detection PPM",
      "Pest control and waste management",
      "Common-area daily cleaning",
      "Security and access control",
    ],
    benchmarks: [
      { v: "30min", l: "Emergency dispatch" },
      { v: "OA", l: "Ready reporting" },
      { v: "24/7", l: "On-site cover" },
      { v: "4.9/5", l: "Resident CSAT" },
    ],
  },
  commercial: {
    eyebrow: "Commercial FM · UAE",
    title_en: "Facility Management for Grade-A Commercial Buildings",
    title_ar: "إدارة المرافق للمباني التجارية من الفئة A",
    subtitle_en: "Integrated FM for offices, HQs and co-working floors in Business Bay, DIFC and Abu Dhabi Global Market — with tenant-grade polish.",
    subtitle_ar: "إدارة مرافق متكاملة للمكاتب والمقرات وطوابق العمل المشترك في الخليج التجاري ومركز دبي المالي وسوق أبوظبي العالمي.",
    image: "https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    pillars_en: [
      { title: "Tenant-Grade Polish", desc: "Lobby, lift-lobby and washroom standards that match your Grade-A rent roll." },
      { title: "Silent Operations", desc: "Off-hours cleaning, day-porter discretion, no workday disruption." },
      { title: "Landlord & Tenant Alignment", desc: "Split scopes, split billing, one contractor — simpler than it sounds." },
      { title: "Energy Optimization", desc: "HVAC scheduling, BMS tuning and monthly energy trending reduce OPEX 8-15%." },
    ],
    scope_en: [
      "Lobby, lift-lobby and washroom deep standards",
      "Day-porter and night cleaning crews",
      "HVAC, chiller and BMS maintenance",
      "MEP, plumbing and electrical PPM",
      "Fire, life safety and compliance",
      "High-level interior and atrium cleaning",
      "Tenant fit-out support and handover",
      "AMC with SLA and monthly reporting",
    ],
    benchmarks: [
      { v: "<1hr", l: "Response time" },
      { v: "15%", l: "Energy OPEX cut" },
      { v: "99.6%", l: "Uptime SLA" },
      { v: "Grade-A", l: "Finish standard" },
    ],
  },
  retail: {
    eyebrow: "Retail & F&B FM · UAE",
    title_en: "Facility Management for Retail, Malls & F&B",
    title_ar: "إدارة المرافق لتجارة التجزئة والمولات والمطاعم",
    subtitle_en: "Front-of-house shine, HACCP-disciplined kitchens and rapid response — across Dubai Mall, Mall of the Emirates and multi-site F&B groups.",
    subtitle_ar: "لمعان الواجهة الأمامية ومطابخ منضبطة بمعايير HACCP واستجابة سريعة — في دبي مول ومول الإمارات ومجموعات المطاعم متعددة المواقع.",
    image: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    pillars_en: [
      { title: "Front-of-House Shine", desc: "Store-opening-ready cleanliness every morning. No scuffs, no streaks, no smell." },
      { title: "HACCP Kitchens", desc: "Food-grade chemicals, scheduled deep cleans and pest programs built for municipal audits." },
      { title: "Multi-Site Standardization", desc: "Same SOP, same standard, same SLA across 5 or 50 outlets." },
      { title: "Peak-Season Surge", desc: "Extra cleaners, helpers and stocking crews for DSF, Ramadan and long weekends." },
    ],
    scope_en: [
      "Store daily housekeeping pre-open and post-close",
      "Back-of-house kitchen deep clean (HACCP)",
      "Grease-trap, drain and extract-hood PPM",
      "Refrigeration and cold-room maintenance",
      "Pest control and waste segregation",
      "Front-of-house glass, marble and signage cleaning",
      "AC chill-water and split-AC PPM",
      "Peak-season manpower surge",
    ],
    benchmarks: [
      { v: "HACCP", l: "Disciplined" },
      { v: "Same-day", l: "Mobilization" },
      { v: "Pre-open", l: "Ready daily" },
      { v: "Multi-site", l: "Standardized" },
    ],
  },
  healthcare: {
    eyebrow: "Healthcare FM · UAE",
    title_en: "Facility Management for Clinics, Pharmacies & Labs",
    title_ar: "إدارة المرافق للعيادات والصيدليات والمختبرات",
    subtitle_en: "Medical-grade cleaning protocols, infection-control SOPs and DHA/DOH compliant housekeeping for healthcare operators across the UAE.",
    subtitle_ar: "بروتوكولات تنظيف بدرجة طبية وإجراءات مكافحة العدوى وتدبير منزلي متوافق مع DHA/DOH لمشغلي الرعاية الصحية.",
    image: "https://images.unsplash.com/photo-1587351021355-a479a299d2f9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    pillars_en: [
      { title: "Infection Control SOPs", desc: "Colour-coded kits, terminal cleans and validated chemicals for clinical environments." },
      { title: "DHA/DOH Alignment", desc: "Procedures mapped to UAE healthcare regulator requirements for audits and inspections." },
      { title: "Trained Clinical Housekeepers", desc: "Not general cleaners — trained on BBP, sharps handling and waste streams." },
      { title: "24/7 Dispatch", desc: "Clinics don't schedule emergencies. Neither do we — on-call response 24/7." },
    ],
    scope_en: [
      "Daily clinical housekeeping (triage, consult, treatment)",
      "Terminal room disinfection and ATP validation",
      "Medical waste handling coordination",
      "HVAC and HEPA-filter PPM",
      "Plumbing, sinks and sluice PPM",
      "Pharmacy and lab cleaning SOPs",
      "Pest control with healthcare-approved products",
      "DHA/DOH inspection-ready documentation",
    ],
    benchmarks: [
      { v: "DHA", l: "Aligned SOPs" },
      { v: "24/7", l: "On-call dispatch" },
      { v: "Validated", l: "Chemicals" },
      { v: "Trained", l: "Clinical staff" },
    ],
  },
  education: {
    eyebrow: "Education FM · UAE",
    title_en: "Facility Management for Schools, Universities & Training Centres",
    title_ar: "إدارة المرافق للمدارس والجامعات ومراكز التدريب",
    subtitle_en: "Child-safe products, term-break deep cleans and KHDA-aligned operations for schools and training campuses across Dubai, Abu Dhabi and Sharjah.",
    subtitle_ar: "منتجات آمنة للأطفال وتنظيف عميق خلال العطلات الدراسية وعمليات متوافقة مع KHDA للمدارس والجامعات.",
    image: "https://images.unsplash.com/photo-1562774053-701939374585?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    pillars_en: [
      { title: "Child-Safe Everything", desc: "Fragrance-free, allergen-free, WHMIS-reviewed products. Nothing that touches a student is risky." },
      { title: "Term-Break PPM", desc: "Deep cleans, AC descaling, fire-system tests and repairs all timed around term calendar." },
      { title: "Campus-Scale Manpower", desc: "Dedicated day porters plus surge crews for exams, open days and graduations." },
      { title: "KHDA & ADEK Alignment", desc: "SOPs and HSE documentation reviewed against UAE education regulator expectations." },
    ],
    scope_en: [
      "Classroom and corridor daily cleaning",
      "Cafeteria, kitchen and nursery deep cleans",
      "Toilets restock and hygiene program",
      "Playground and outdoor equipment checks",
      "HVAC, plumbing and electrical PPM",
      "Fire safety, emergency lighting and PA tests",
      "Bus and transport interior cleaning",
      "Term-break mobilization (HVAC descale, paint, repairs)",
    ],
    benchmarks: [
      { v: "Child-safe", l: "Product stack" },
      { v: "KHDA", l: "Aligned" },
      { v: "Term-break", l: "PPM timed" },
      { v: "Campus", l: "Scale manpower" },
    ],
  },
};

export const SERVICE_DETAILS = {
  "facility-management": {
    image:
      "https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    benefits_en: [
      "Single SLA-driven contract — replaces 5+ vendors",
      "PPM calendar covering HVAC, MEP, plumbing, fire, electrical",
      "Soft FM bundled: cleaning, pest control, landscaping, waste",
      "24/7 dedicated control room and emergency response",
      "Monthly KPI reporting and quarterly business reviews",
    ],
    benefits_ar: [
      "عقد واحد ملتزم باتفاقية مستوى الخدمة - يستبدل 5 موردين أو أكثر",
      "تقويم صيانة وقائية يغطي التكييف والكهروميكانيك والسباكة والحرائق والكهرباء",
      "خدمات صيانة لينة مدمجة: تنظيف، مكافحة آفات، مناظر طبيعية، نفايات",
      "غرفة تحكم مخصصة على مدار الساعة واستجابة للطوارئ",
      "تقارير مؤشرات الأداء الرئيسية الشهرية ومراجعات الأعمال الربع سنوية",
    ],
    useCases_en: [
      "Grade-A commercial towers in Business Bay & DIFC",
      "Logistics and 3PL warehouses in JAFZA, DIP & KIZAD",
      "Mixed-use residential communities across Dubai & Abu Dhabi",
      "Retail and F&B portfolios with multi-site footprints",
    ],
    useCases_ar: [
      "أبراج تجارية من الفئة A في الخليج التجاري ومركز دبي المالي العالمي",
      "مستودعات لوجستية في جافزا ومجمع دبي للاستثمار وكيزاد",
      "مجتمعات سكنية مختلطة الاستخدام في جميع أنحاء دبي وأبوظبي",
      "محافظ التجزئة والمطاعم متعددة المواقع",
    ],
    faqs_en: [
      { q: "What's the difference between hard and soft FM?", a: "Hard FM covers building systems (HVAC, MEP, fire, electrical, plumbing). Soft FM covers occupant services (cleaning, pest, landscaping, waste). Orion delivers both under one contract." },
      { q: "How fast is your emergency response?", a: "SLA-bound: 30 minutes for critical (life-safety, flooding), 2 hours for major, 8 hours for minor. Response times are tracked and reported monthly." },
      { q: "Do you handle multi-site portfolios?", a: "Yes. Our delivery model is built for multi-site clients — single SPOC, consolidated reporting, standardized SLAs across sites." },
    ],
    faqs_ar: [
      { q: "ما الفرق بين إدارة المرافق الصلبة واللينة؟", a: "تغطي إدارة المرافق الصلبة أنظمة المبنى (التكييف والكهروميكانيك والحرائق). تغطي إدارة المرافق اللينة خدمات شاغلي المبنى (التنظيف ومكافحة الآفات). تقدم أوريون كليهما تحت عقد واحد." },
      { q: "ما مدى سرعة استجابتكم للطوارئ؟", a: "ملتزمة باتفاقية مستوى الخدمة: 30 دقيقة للطوارئ الحرجة، ساعتان للكبرى، 8 ساعات للصغرى. يتم تتبع أوقات الاستجابة والإبلاغ عنها شهريًا." },
      { q: "هل تتعاملون مع المحافظ متعددة المواقع؟", a: "نعم. نموذج التسليم لدينا مصمم للعملاء متعددي المواقع - نقطة اتصال واحدة، تقارير موحدة، اتفاقيات مستوى خدمة موحدة عبر المواقع." },
    ],
  },
  cleaning: {
    image:
      "https://images.unsplash.com/photo-1581578731548-c64695cc6952?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    benefits_en: [
      "Daily, weekly, monthly and one-off programs",
      "Specialized: deep cleaning, post-construction and disinfection",
      "Eco-friendly chemicals approved for UAE municipalities",
      "Trained, uniformed and DM-licensed cleaning staff",
      "Live attendance tracking and supervisor checklists",
    ],
    benefits_ar: [
      "برامج يومية وأسبوعية وشهرية ومرة واحدة",
      "متخصص: تنظيف عميق، ما بعد البناء، وتعقيم",
      "مواد كيميائية صديقة للبيئة معتمدة من بلديات الإمارات",
      "موظفو تنظيف مدربون وبزي موحد ومرخصون من بلدية دبي",
      "تتبع الحضور المباشر وقوائم المراجعة الإشرافية",
    ],
    useCases_en: [
      "Office daily housekeeping and day-porter services",
      "Warehouse high-bay vacuuming and forklift-aisle scrubbing",
      "Hotel and serviced-apartment turnover cleaning",
      "Post-construction handover deep cleans",
    ],
    useCases_ar: [
      "تدبير منزلي يومي للمكاتب وخدمات الحاجب النهاري",
      "شفط الرفوف العالية في المستودعات وغسل ممرات الرافعات",
      "تنظيف الفنادق والشقق المخدومة عند تغيير النزلاء",
      "تنظيف عميق لتسليم ما بعد البناء",
    ],
    faqs_en: [
      { q: "Are your products safe for healthcare and food sites?", a: "Yes. We carry HACCP and medical-grade SOPs and use products certified for those environments." },
      { q: "Can you do night cleaning?", a: "Absolutely. Most of our commercial and retail clients are cleaned 9pm–6am to avoid disrupting operations." },
      { q: "Do you provide your own equipment?", a: "Yes. Vacuums, scrubber-driers, sweepers, ride-ons and high-reach access equipment are all included." },
    ],
    faqs_ar: [
      { q: "هل منتجاتكم آمنة للمواقع الصحية والغذائية؟", a: "نعم. نحمل إجراءات HACCP والإجراءات الطبية ونستخدم منتجات معتمدة لهذه البيئات." },
      { q: "هل يمكنكم إجراء التنظيف الليلي؟", a: "بالتأكيد. يتم تنظيف معظم عملائنا التجاريين من 9 مساءً إلى 6 صباحًا لتجنب تعطيل العمليات." },
      { q: "هل توفرون معداتكم الخاصة؟", a: "نعم. تشمل المكانس الكهربائية وآلات الغسيل والكنس ومعدات الوصول إلى الأماكن المرتفعة." },
    ],
  },
  maintenance: {
    image:
      "https://images.unsplash.com/photo-1581094288338-2314dddb7ece?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    benefits_en: [
      "AC servicing, descaling, gas-recharge and chiller PPM",
      "Electrical, plumbing, carpentry and painting",
      "Installation: split AC, light fixtures, joinery and partitions",
      "Certified DEWA, ADDC and SEWA-registered technicians",
      "Same-day callouts across Dubai, Abu Dhabi and Sharjah",
    ],
    benefits_ar: [
      "صيانة التكييف وإزالة الترسبات وإعادة شحن الغاز وصيانة المبردات الوقائية",
      "كهرباء، سباكة، نجارة ودهان",
      "التركيب: تكييف منفصل، تركيبات إضاءة، نجارة وقواطع",
      "فنيون معتمدون مسجلون لدى ديوا و ADDC و SEWA",
      "مكالمات في نفس اليوم في جميع أنحاء دبي وأبوظبي والشارقة",
    ],
    useCases_en: [
      "Summer AC PPM ahead of 50C peak season",
      "Reactive emergency repairs (leak, no-cool, power-out)",
      "Annual fire-system inspection and certification",
      "Office and villa fit-out and handover",
    ],
    useCases_ar: [
      "صيانة وقائية للتكييف قبل موسم الذروة (50 درجة مئوية)",
      "إصلاحات طارئة تفاعلية (تسرب، عدم تبريد، انقطاع الطاقة)",
      "فحص واعتماد نظام الحرائق السنوي",
      "تجهيز وتسليم المكاتب والفلل",
    ],
    faqs_en: [
      { q: "How often should AC be serviced in the UAE?", a: "We recommend every 3 months for residential and monthly PPM for commercial. UAE dust and heat accelerate wear significantly." },
      { q: "Are emergency call-outs available 24/7?", a: "Yes. AMC clients get 24/7 emergency response. Walk-in customers get same-day daytime support." },
      { q: "Do you handle fire and life-safety systems?", a: "Yes. We are certified to inspect, test and maintain fire alarm, sprinkler and emergency lighting systems." },
    ],
    faqs_ar: [
      { q: "كم مرة يجب صيانة التكييف في الإمارات؟", a: "نوصي كل 3 أشهر للسكنية وصيانة وقائية شهرية للتجارية. الغبار والحرارة في الإمارات يعجلان من التآكل بشكل كبير." },
      { q: "هل خدمات الطوارئ متاحة على مدار الساعة؟", a: "نعم. يحصل عملاء AMC على استجابة طوارئ على مدار الساعة." },
      { q: "هل تتعاملون مع أنظمة السلامة من الحرائق والحياة؟", a: "نعم. نحن معتمدون لفحص واختبار وصيانة أنظمة إنذار الحريق والرشاشات وإضاءة الطوارئ." },
    ],
  },
  manpower: {
    image:
      "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    benefits_en: [
      "Cleaners, technicians, helpers, drivers, supervisors",
      "Long-term, short-term and surge deployment",
      "All staff sponsored, insured and HSE-trained by Orion",
      "Background-checked, uniformed and ID-badged",
      "Replacement guarantee within 24 hours if a profile doesn't fit",
    ],
    benefits_ar: [
      "عمال نظافة، فنيون، مساعدون، سائقون، مشرفون",
      "نشر طويل الأجل وقصير الأجل ومتزايد",
      "جميع الموظفين مكفولون ومؤمن عليهم ومدربون على الصحة والسلامة والبيئة",
      "تم التحقق من الخلفيات وبزي موحد وشارات هوية",
      "ضمان الاستبدال خلال 24 ساعة إذا كان الملف الشخصي لا يناسب",
    ],
    useCases_en: [
      "Peak-season retail surge (DSF, Eid, Ramadan)",
      "Event manpower for stadiums, conferences and exhibitions",
      "Project mobilization for construction sites",
      "Long-term outsourced housekeeping and security teams",
    ],
    useCases_ar: [
      "زيادة موسم الذروة في التجزئة (مهرجان دبي للتسوق، العيد، رمضان)",
      "قوى عاملة للفعاليات للملاعب والمؤتمرات والمعارض",
      "تعبئة المشاريع لمواقع البناء",
      "فرق التدبير المنزلي والأمن الخارجية طويلة الأجل",
    ],
    faqs_en: [
      { q: "What's your minimum deployment size?", a: "We deploy from 1 staff member to 500+ within 72 hours, depending on profile and location." },
      { q: "Are you a licensed manpower supplier?", a: "Yes. We hold UAE manpower-supply trade licenses and all staff are sponsored and insured under Orion." },
      { q: "Can we interview candidates before deployment?", a: "Of course. For long-term placements we share profiles and arrange interviews." },
    ],
    faqs_ar: [
      { q: "ما هو الحد الأدنى لحجم النشر؟", a: "نحن ننشر من موظف واحد إلى 500+ في غضون 72 ساعة، حسب الملف الشخصي والموقع." },
      { q: "هل أنتم مورد قوى عاملة مرخص؟", a: "نعم. نحن نحمل تراخيص تجارية لتوريد القوى العاملة في الإمارات." },
      { q: "هل يمكننا مقابلة المرشحين قبل النشر؟", a: "بالطبع. للتعيينات طويلة الأجل، نشارك الملفات الشخصية ونرتب المقابلات." },
    ],
  },
};
